import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { HelpCircle, Edit } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { v4 as uuidv4 } from "uuid";
import { getFirebaseStorage, useDebounced } from "@/utils";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { ConfigContext } from "@/contexts/appContexts";
import { UploadProgressModal } from "@/components/UploadProgressModal";
import { validationSchema } from "./validations";
import { VideoPagePreview } from "@/containers";
import { MediaUploader } from "./MediaUploader";
import { MediaItem } from "./MediaItem";
import { EqualHousingLenderLogo, EqualHousingOpportunityLogo } from "../EqualHousingLogos";
import { ContentPageThemeRequest, ContentPageThemeServices } from "@/services/bedrock/ContentPageThemeServices";
import { ContentPageTheme, Media } from "@/services/bedrock";
import { toast } from "sonner";
import { Button, Input, Field, Popover, Dialog, Alert, InputGroup, Switch, Textarea } from "@/components/ui";

interface VideoPageThemeFormProps {
  selectedTheme: ContentPageTheme | undefined;
  goBack: () => void;
  onUpdate: (theme: ContentPageTheme) => void;
}

interface ThemeColor {
  label: string;
  name: keyof FormValues;
}

interface FormValues {
  name: string;
  backgroundColor: string;
  textColor: string;
  primaryCtaColor: string;
  primaryCtaTextColor: string;
  headerMedia: Media[];
  footerMedia: Media[];
  showEhLenderLogo: boolean;
  showEhOpportunityLogo: boolean;
  disclaimer: string;
}

type Props = VideoPageThemeFormProps;

const ensureHashPrefix = (color: string | undefined, defaultColor: string) => {
  if (!color) return defaultColor;
  return color.startsWith("#") ? color : `#${color}`;
};

export const VideoPageThemeForm: React.FC<Props> = ({ selectedTheme, goBack, onUpdate }) => {
  const [showColorModal, setShowColorModal] = useState<boolean>(false);
  const colorFields: ThemeColor[] = [
    { label: "Background Color", name: "backgroundColor" },
    { label: "Text Color", name: "textColor" },
    { label: "Primary CTA Color", name: "primaryCtaColor" },
    { label: "Primary CTA Text Color", name: "primaryCtaTextColor" },
  ];
  const [themeColor, setThemeColor] = useState<ThemeColor>(colorFields[0]);
  const [color, setColor] = useState<string>("#000000");
  const [uploadProgressMap, setUploadProgressMap] = React.useState<any>();
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const { firebase } = React.useContext(ConfigContext);

  // Logo cropping states
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropSrc, setCropSrc] = useState<string>("");
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [croppingTarget, setCroppingTarget] = useState<"header" | "footer">("header");
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    x: 10,
    y: 10,
    width: 80,
    height: 80,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const [tempMedia, setTempMedia] = useState<Media | undefined>(undefined);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema) as any,
    mode: "all",
    defaultValues: {
      name: selectedTheme?.name || "New Theme",
      backgroundColor: ensureHashPrefix(selectedTheme?.backgroundColor, "#000000"),
      textColor: ensureHashPrefix(selectedTheme?.textColor, "#FFFFFF"),
      primaryCtaColor: ensureHashPrefix(selectedTheme?.primaryCtaColor, "#FFFFFF"),
      primaryCtaTextColor: ensureHashPrefix(selectedTheme?.primaryCtaTextColor, "#000000"),
      headerMedia: selectedTheme?.headerMedia || [],
      footerMedia: selectedTheme?.footerMedia || [],
      showEhLenderLogo: selectedTheme?.showEhLenderLogo || false,
      showEhOpportunityLogo: selectedTheme?.showEhOpportunityLogo || false,
      disclaimer: selectedTheme?.disclaimer || "",
    },
  });

  const values = watch();
  const debouncedDisclaimer = useDebounced(values.disclaimer, 500);

  React.useEffect(() => {
    reset({
      name: selectedTheme?.name || "New Theme",
      backgroundColor: ensureHashPrefix(selectedTheme?.backgroundColor, "#000000"),
      textColor: ensureHashPrefix(selectedTheme?.textColor, "#FFFFFF"),
      primaryCtaColor: ensureHashPrefix(selectedTheme?.primaryCtaColor, "#FFFFFF"),
      primaryCtaTextColor: ensureHashPrefix(selectedTheme?.primaryCtaTextColor, "#000000"),
      headerMedia: selectedTheme?.headerMedia || [],
      footerMedia: selectedTheme?.footerMedia || [],
      showEhLenderLogo: selectedTheme?.showEhLenderLogo || false,
      showEhOpportunityLogo: selectedTheme?.showEhOpportunityLogo || false,
      disclaimer: selectedTheme?.disclaimer || "",
    });
  }, [selectedTheme, reset]);

  const saveFile = async (file: File): Promise<string> => {
    setUploading(true);
    const extension = file.type.split("/").pop();
    const filename = `${uuidv4()}.${extension}`;
    const destUri = `${firebase.baseImagesFolder}/contentPages/themes/${filename}`;
    const storage = getFirebaseStorage(firebase);
    const storageRef = ref(storage, destUri);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on("state_changed", snapshot => {
      const uploadProgress = snapshot.bytesTransferred / snapshot.totalBytes;
      setUploadProgressMap({ 1: { progress: uploadProgress, task: undefined } });
    });

    await uploadTask;
    const uri = await getDownloadURL(storageRef);
    setUploading(false);
    return uri;
  };

  const getCroppedImg = (image: HTMLImageElement, pixelCrop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = Math.floor(pixelCrop.width * scaleX);
    canvas.height = Math.floor(pixelCrop.height * scaleY);

    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/png",
        1
      );
    });
  };

  const handleApplyCrop = async () => {
    if (imageRef.current && completedCrop && completedCrop.width && completedCrop.height) {
      try {
        const croppedBlob = await getCroppedImg(imageRef.current, completedCrop);
        const croppedFile = new File([croppedBlob], cropFile?.name || "cropped-logo.png", {
          type: "image/png",
        });

        setShowCropModal(false);
        setCropFile(null);
        setCropSrc("");

        const uri = await saveFile(croppedFile);
        const newMediaItem = {
          ...tempMedia,
          uri,
        } as Media;

        if (croppingTarget === "header") {
          setValue("headerMedia", [newMediaItem], { shouldValidate: true });
        } else {
          const currentFooterMedia = values.footerMedia || [];
          newMediaItem.sortOrder = currentFooterMedia.length;
          setValue("footerMedia", [...currentFooterMedia, newMediaItem], { shouldValidate: true });
        }
      } catch (e) {
        console.error("Error generating cropped image", e);
        toast.error("Failed to crop image.");
      }
    } else {
      if (cropFile && tempMedia) {
        setShowCropModal(false);
        const uri = await saveFile(cropFile);
        const newMediaItem = {
          ...tempMedia,
          uri,
        } as Media;

        if (croppingTarget === "header") {
          setValue("headerMedia", [newMediaItem], { shouldValidate: true });
        } else {
          const currentFooterMedia = values.footerMedia || [];
          newMediaItem.sortOrder = currentFooterMedia.length;
          setValue("footerMedia", [...currentFooterMedia, newMediaItem], { shouldValidate: true });
        }
        setCropFile(null);
        setCropSrc("");
      }
    }
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setCropFile(null);
    setCropSrc("");
    setTempMedia(undefined);
  };

  const onSubmitForm = async (data: FormValues) => {
    setSaving(true);
    const request: ContentPageThemeRequest = {
      name: data.name,
      backgroundColor: ensureHashPrefix(data.backgroundColor, "#000000"),
      textColor: ensureHashPrefix(data.textColor, "#FFFFFF"),
      primaryCtaColor: ensureHashPrefix(data.primaryCtaColor, "#FFFFFF"),
      primaryCtaTextColor: ensureHashPrefix(data.primaryCtaTextColor, "#000000"),
      headerMedia: data.headerMedia,
      footerMedia: data.footerMedia,
      showEhLenderLogo: data.showEhLenderLogo,
      showEhOpportunityLogo: data.showEhOpportunityLogo,
      disclaimer: data.disclaimer,
    };

    try {
      const newTheme = await (!!selectedTheme?.id
        ? ContentPageThemeServices.updatePageTheme(selectedTheme.id, request)
        : ContentPageThemeServices.createPageTheme(request));
      toast.success("Theme saved successfully!");
      onUpdate(newTheme);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save theme.");
    } finally {
      setSaving(false);
    }
  };

  const errorMessages = Object.values(errors)
    .map(e => e?.message)
    .filter(Boolean) as string[];

  return (
    <div className="w-full min-h-[500px]">
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        {errorMessages.length > 0 && (
          <Alert variant="destructive">
            <Alert.Title>Validation Errors</Alert.Title>
            <Alert.Description>
              <ul className="list-disc pl-4 space-y-1">
                {errorMessages.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </Alert.Description>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 items-start">
          {/* Left Column - Form Fields */}
          <div className="space-y-6 bg-card border border-border p-6 rounded-xl shadow-sm">
            <Field label="Theme Name" error={errors.name?.message}>
              <Input id="name" placeholder="Theme Name" {...register("name")} />
            </Field>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-foreground select-none">Colors</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {colorFields.map(field => (
                  <div
                    key={field.name}
                    className="flex justify-between items-center p-3.5 hover:bg-muted/10 transition-colors border border-slate-300 dark:border-slate-700 rounded-lg bg-muted/5 shadow-xs"
                  >
                    <span className="text-xs font-semibold text-muted-foreground pr-2 leading-tight">
                      {field.label}
                    </span>
                    <div className="flex items-center gap-2.5 shrink-0">
                      <div
                        className="w-10 h-10 rounded-full border border-border shadow-sm shrink-0"
                        style={{ backgroundColor: values[field.name] as string }}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        onClick={() => {
                          setThemeColor(field);
                          setColor(values[field.name] as string);
                          setShowColorModal(true);
                        }}
                      >
                        <Edit className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <label className="text-sm font-semibold text-foreground select-none">Header Logo</label>
                <Popover>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors ml-1.5 focus:outline-none"
                    >
                      <HelpCircle className="size-4" />
                    </button>
                  </Popover.Trigger>
                  <Popover.Content className="w-80 p-4">
                    <h4 className="font-semibold text-sm mb-1.5 text-foreground">Tips for a good header image</h4>
                    <ul className="text-xs space-y-1.5 list-disc pl-4 text-muted-foreground">
                      <li>
                        <strong>Crop Tightly</strong>: Before uploading, trim extra space around your logo or image.
                      </li>
                      <li>
                        <strong>Wider is Better</strong>: Landscape (wide, not tall) logos fit best.
                      </li>
                      <li>
                        <strong>Blend Background</strong>: Use a transparent png, or match your theme's background
                        color.
                      </li>
                    </ul>
                  </Popover.Content>
                </Popover>
              </div>

              <MediaUploader
                media={values.headerMedia?.[0]}
                onUpdateMedia={(newMedia, newFile) => {
                  if (newFile) {
                    if (newMedia?.category === "IMAGE") {
                      setTempMedia(newMedia);
                      setCropFile(newFile);
                      setCropSrc(URL.createObjectURL(newFile));
                      setCroppingTarget("header");
                      setShowCropModal(true);
                    } else {
                      saveFile(newFile).then(uri => {
                        setValue(
                          "headerMedia",
                          [
                            {
                              ...newMedia,
                              uri,
                            },
                          ] as Media[],
                          { shouldValidate: true }
                        );
                      });
                    }
                  } else {
                    setValue("headerMedia", [], { shouldValidate: true });
                  }
                }}
              />
            </div>

            {/* Footer Section */}
            <div className="space-y-6">
              <h3 className="text-base font-bold text-foreground select-none">Footer</h3>

              {/* Footer Logos Configuration Block */}
              <div className="space-y-4">
                <div className="flex flex-col gap-1 select-none">
                  <span className="text-sm font-semibold text-foreground">Footer Logos</span>
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    These standard compliance logos automatically adapt to your selected theme colors to guarantee
                    maximum contrast and readability. You can also upload custom brand or affiliate logos below.
                  </span>
                </div>

                {/* Grid for Compliance Logos (takes up full available width, split evenly) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                  {/* Equal Housing Lender Card */}
                  <div
                    onClick={() => {
                      const nextVal = !values.showEhLenderLogo;
                      setValue("showEhLenderLogo", nextVal, { shouldValidate: true });
                      if (nextVal) {
                        setValue("showEhOpportunityLogo", false, { shouldValidate: true });
                      }
                    }}
                    className={`flex flex-row items-center justify-between border rounded-xl p-4 bg-muted/10 shadow-xs h-[130px] gap-4 cursor-pointer select-none transition-all duration-150 ${
                      values.showEhLenderLogo
                        ? "border-primary ring-1 ring-primary/25 bg-primary/[0.03]"
                        : "border-border/80 hover:border-slate-300 dark:hover:border-slate-700"
                    }`}
                  >
                    <div className="flex flex-col gap-2 flex-1 items-start justify-between h-full">
                      <div className="flex flex-col gap-0.5 select-none">
                        <span className="text-xs font-bold text-foreground leading-tight">Equal Housing Lender</span>
                        <span className="text-xs font-medium text-muted-foreground leading-tight">
                          Display in footer
                        </span>
                      </div>

                      <div
                        className="p-2 rounded border border-border/45 inline-flex max-w-max transition-all shadow-xs opacity-75"
                        style={{
                          backgroundColor: values.backgroundColor,
                        }}
                      >
                        <EqualHousingLenderLogo textColor={values.textColor} />
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center pr-1" onClick={e => e.stopPropagation()}>
                      <Switch
                        checked={values.showEhLenderLogo}
                        onCheckedChange={checked => {
                          setValue("showEhLenderLogo", checked, { shouldValidate: true });
                          if (checked) {
                            setValue("showEhOpportunityLogo", false, { shouldValidate: true });
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Equal Opportunity Housing Card */}
                  <div
                    onClick={() => {
                      const nextVal = !values.showEhOpportunityLogo;
                      setValue("showEhOpportunityLogo", nextVal, { shouldValidate: true });
                      if (nextVal) {
                        setValue("showEhLenderLogo", false, { shouldValidate: true });
                      }
                    }}
                    className={`flex flex-row items-center justify-between border rounded-xl p-4 bg-muted/10 shadow-xs h-[130px] gap-4 cursor-pointer select-none transition-all duration-150 ${
                      values.showEhOpportunityLogo
                        ? "border-primary ring-1 ring-primary/25 bg-primary/[0.03]"
                        : "border-border/80 hover:border-slate-300 dark:hover:border-slate-700"
                    }`}
                  >
                    <div className="flex flex-col gap-2 flex-1 items-start justify-between h-full">
                      <div className="flex flex-col gap-0.5 select-none">
                        <span className="text-xs font-bold text-foreground leading-tight">
                          Equal Opportunity Housing
                        </span>
                        <span className="text-xs font-medium text-muted-foreground leading-tight">
                          Display in footer
                        </span>
                      </div>

                      <div
                        className="p-2 rounded border border-border/45 inline-flex max-w-max transition-all shadow-xs opacity-75"
                        style={{
                          backgroundColor: values.backgroundColor,
                        }}
                      >
                        <EqualHousingOpportunityLogo textColor={values.textColor} />
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center pr-1" onClick={e => e.stopPropagation()}>
                      <Switch
                        checked={values.showEhOpportunityLogo}
                        onCheckedChange={checked => {
                          setValue("showEhOpportunityLogo", checked, { shouldValidate: true });
                          if (checked) {
                            setValue("showEhLenderLogo", false, { shouldValidate: true });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional brand logos container */}
                <div className="space-y-2 flex flex-col gap-1">
                  <label className="text-sm font-semibold text-foreground select-none">Additional Brand Logos</label>
                  <div className="flex flex-wrap gap-4 items-center border border-border/80 rounded-xl p-4 bg-muted/10">
                    {(values.footerMedia || []).map((mediaItem, index) => (
                      <div key={`footer-media-${index}`} className="h-[130px] flex items-stretch">
                        <MediaItem
                          src={mediaItem}
                          id={index}
                          handleRemove={idx => {
                            const updated = (values.footerMedia || [])
                              .filter((_, i) => i !== idx)
                              .map((item, i) => ({ ...item, sortOrder: i }));
                            setValue("footerMedia", updated, { shouldValidate: true });
                          }}
                        />
                      </div>
                    ))}

                    {/* Modernized Card-Style Logo Uploader */}
                    <MediaUploader
                      media={undefined}
                      isCardStyle={true}
                      onUpdateMedia={(newMedia, newFile) => {
                        if (newFile) {
                          if (newMedia?.category === "IMAGE") {
                            setTempMedia(newMedia);
                            setCropFile(newFile);
                            setCropSrc(URL.createObjectURL(newFile));
                            setCroppingTarget("footer");
                            setShowCropModal(true);
                          } else {
                            saveFile(newFile).then(uri => {
                              const currentFooterMedia = values.footerMedia || [];
                              const updated = [
                                ...currentFooterMedia,
                                {
                                  ...newMedia,
                                  uri,
                                  sortOrder: currentFooterMedia.length,
                                },
                              ] as Media[];
                              setValue("footerMedia", updated, { shouldValidate: true });
                            });
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="space-y-2">
                <div className="flex flex-col gap-0.5 mb-1 select-none">
                  <span className="text-sm font-semibold text-foreground">Brand Disclaimer Text</span>
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    This can be used to set brand-specific disclaimer text that will be displayed in the footer.
                  </span>
                </div>
                <Field error={errors.disclaimer?.message}>
                  <Textarea
                    id="disclaimer"
                    placeholder="Enter disclaimer text to display in the footer..."
                    {...register("disclaimer")}
                  />
                </Field>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button variant="outline" type="button" onClick={goBack} disabled={saving}>
                Discard Changes
              </Button>
              <Button type="submit" loading={saving}>
                Save
              </Button>
            </div>
          </div>

          {/* Right Column - Live Preview */}
          <div className="lg:sticky lg:top-6 bg-card border border-border p-6 rounded-xl shadow-sm space-y-4">
            <h3 className="text-sm font-semibold text-foreground select-none border-b border-border pb-2">
              Live Preview
            </h3>
            <div className="flex justify-center bg-muted/20 p-4 rounded-lg overflow-hidden border border-border/40">
              <VideoPagePreview
                slug="preview"
                backgroundColor={values.backgroundColor}
                textColor={values.textColor}
                primaryCtaColor={values.primaryCtaColor}
                primaryCtaTextColor={values.primaryCtaTextColor}
                headerUri={values.headerMedia?.[0]?.uri}
                footerUris={(values.footerMedia || []).map(m => m.uri).filter(Boolean)}
                showEhLenderLogo={values.showEhLenderLogo}
                showEhOpportunityLogo={values.showEhOpportunityLogo}
                disclaimer={debouncedDisclaimer}
              />
            </div>
          </div>
        </div>
      </form>

      {/* Hex Color Picker Dialog */}
      <Dialog open={showColorModal} onOpenChange={setShowColorModal}>
        <Dialog.Header>
          <Dialog.Title>Edit {themeColor.label}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="flex flex-col items-center justify-center p-6 gap-4">
          <HexColorPicker color={color} onChange={setColor} className="w-full rounded-md shadow-sm" />
          <div className="flex items-center gap-3 w-full max-w-[240px] mt-2 justify-center">
            <div
              className="w-10 h-10 rounded-full border border-border shadow-sm shrink-0"
              style={{ backgroundColor: color }}
            />
            <InputGroup
              prepend="#"
              className="w-full max-w-[140px]"
              control={controlProps => (
                <Input
                  {...controlProps}
                  value={color.startsWith("#") ? color.slice(1) : color}
                  onChange={e => {
                    const typed = e.target.value;
                    setColor(typed.startsWith("#") ? typed : `#${typed}`);
                  }}
                  className="font-mono text-center uppercase"
                />
              )}
            />
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button variant="outline" type="button" onClick={() => setShowColorModal(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const trimmedColor = (color || "").trim();
              const formattedColor = ensureHashPrefix(trimmedColor, "#000000");
              setValue(themeColor.name, formattedColor, { shouldValidate: true });
              setShowColorModal(false);
            }}
          >
            Done
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* Logo Cropping Dialog */}
      <Dialog
        open={showCropModal}
        onOpenChange={open => {
          if (!open) handleCancelCrop();
        }}
        size="lg"
      >
        <Dialog.Header>
          <Dialog.Title>Crop Header Logo</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="flex flex-col gap-4 p-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Please crop your logo <strong>tightly around its borders</strong>. Do not leave extra empty, transparent, or
            white margins around the edges, as we will automatically add the correct amount of standard padding when
            rendering it on your page to ensure it looks perfectly balanced!
          </p>

          <div className="flex justify-center max-h-[400px] overflow-auto border border-border bg-slate-50/50 p-4 rounded-lg">
            {cropSrc && (
              <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)}>
                <img
                  ref={imageRef}
                  src={cropSrc}
                  alt="Source Logo"
                  className="max-w-full max-h-[350px] object-contain"
                />
              </ReactCrop>
            )}
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button variant="outline" type="button" onClick={handleCancelCrop}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApplyCrop}>
            Confirm & Crop Logo
          </Button>
        </Dialog.Footer>
      </Dialog>

      <UploadProgressModal header="Uploading Header" open={uploading} progressMap={uploadProgressMap} />
    </div>
  );
};
